<template>
  <transition name="modal-fade">
    <div v-if="isOpen" class="agreement-modal-overlay" @click.self="close">
      <div class="agreement-modal-content">
        <div class="agreement-header">
          <h2 class="agreement-title">{{ title }}</h2>
          <button class="agreement-close-btn" @click="close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div class="agreement-body">
          <div class="agreement-text">
            <div v-if="loading" class="loading-text">{{ $t('agreementModal.loading') }}</div>
            <div v-else-if="error" class="error-text">{{ error }}</div>
            <MarkdownRenderer v-else :content="markdownContent" />
          </div>
        </div>
        <div class="agreement-footer">
          <button class="agreement-confirm-btn" @click="close">{{ $t('agreementModal.understood') }}</button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import MarkdownRenderer from './MarkdownRenderer.vue';

export default {
  name: 'AgreementModal',
  components: {
    MarkdownRenderer
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    type: {
      type: String, // 'privacy' or 'terms'
      default: 'privacy'
    }
  },
  data() {
    return {
      contentCache: {
        privacy: '',
        terms: ''
      },
      loading: false,
      error: null
    };
  },
  computed: {
    title() {
      return this.$t(this.type === 'privacy' ? 'agreementModal.privacyTitle' : 'agreementModal.termsTitle');
    },
    markdownContent() {
      return this.contentCache[this.type] || '';
    }
  },
  watch: {
    isOpen: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.loadContent();
        }
      }
    },
    type: {
      handler() {
        if (this.isOpen) {
          this.loadContent();
        }
      }
    }
  },
  methods: {
    close() {
      this.$emit('close');
    },
    getContentUrls(currentType) {
      const baseName = currentType === 'privacy' ? 'yinsi' : 'service';
      const locale = this.$i18n && this.$i18n.locale === 'en' ? 'en' : 'zh';

      if (locale === 'en') {
        return [`/${baseName}.en.md`, `/${baseName}.md`];
      }

      return [`/${baseName}.md`];
    },
    async loadContent() {
      const currentType = this.type;
      if (this.contentCache[currentType]) return;
      
      this.loading = true;
      this.error = null;
      try {
        const urls = this.getContentUrls(currentType);
        let text = '';

        for (const url of urls) {
          const response = await fetch(url);
          if (response.ok) {
            text = await response.text();
            break;
          }
        }

        if (!text) {
          throw new Error('Failed to load content');
        }

        this.contentCache[currentType] = text;
      } catch (err) {
        this.error = this.$t('agreementModal.loadFailed', { title: this.title });
        console.error(`Error loading ${currentType} content:`, err);
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>

<style scoped>
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.agreement-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000001;
  backdrop-filter: blur(4px);
}

.agreement-modal-content {
  background: white;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  animation: slide-up 0.3s ease-out;
}

@keyframes slide-up {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.agreement-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #f0f0f0;
}

.agreement-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1c1c1e;
}

.agreement-close-btn {
  background: none;
  border: none;
  color: #8e8e93;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.agreement-close-btn:hover {
  background: #f2f2f7;
  color: #1c1c1e;
}

.agreement-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.agreement-text {
  color: #3a3a3c;
  font-size: 14px;
  line-height: 1.6;
}

.agreement-text h3 {
  font-size: 15px;
  font-weight: 600;
  color: #1c1c1e;
  margin: 20px 0 10px;
}

.agreement-text h3:first-child {
  margin-top: 0;
}

.agreement-text p {
  margin: 0 0 12px;
  color: #636366;
}

.agreement-footer {
  padding: 16px 24px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: flex-end;
}

.agreement-confirm-btn {
  background: #007aff;
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}

.agreement-confirm-btn:hover {
  background: #0056b3;
}
</style>
